import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Loader2, Edit3, Box, Eye, Cpu, CheckCircle2, XCircle,
  ChevronDown, ChevronRight, FileCode, FileText, Play, Sparkles,
  AlertTriangle, Zap,
} from 'lucide-react'
import {
  getJson, generateModel, generatePrototype, generateDatabase,
  showPrototype, checkModelStatus, checkPrototypeStatus,
  getFrsContent, generateAppFromFrs,
} from '@/api/generateapp'
import { generatePageObject, generateVisualObject } from '@/api/designers'
import { saveDraft, getProjectReferences, setProjectReferences } from '@/api/workbench'
import { getSettings } from '@/api/settings'
import { useProjectStore } from '@/store/projectStore'
import { useClaudeAgent } from '@/hooks/useClaudeAgent'
import AgentStreamPanel from '@/components/common/AgentStreamPanel'
import ReferenceProjectPicker from '@/components/common/ReferenceProjectPicker'
import AppIcon from '@/components/common/AppIcon'
import type { ProjectData, NavigationItem, NavigationObject, ProjectReference } from '@/types'

interface ObjectStatus {
  modelExists: boolean
  prototypeExists: boolean
  generatingModel: boolean
  generatingPrototype: boolean
}

export default function PreviewAppPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const project = searchParams.get('project') || 'Draft Application'
  const { fetchProjects, currentProject, selectProject } = useProjectStore()

  // Restore currentProject in store from URL param on refresh
  useEffect(() => {
    if (project && project !== 'Draft Application' && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [appData, setAppData] = useState<ProjectData | null>(null)
  const [loading, setLoading] = useState(true)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [objectStatuses, setObjectStatuses] = useState<Record<string, ObjectStatus>>({})
  const [prototypeHtml, setPrototypeHtml] = useState<string | null>(null)
  const [prototypeObjName, setPrototypeObjName] = useState<string | null>(null)
  const [generationMode, setGenerationMode] = useState<string>('llm')
  const [effortConfig, setEffortConfig] = useState<Record<string, string>>({})
  const [agentPanelOpen, setAgentPanelOpen] = useState(false)
  const [agentTargetObj, setAgentTargetObj] = useState<string | null>(null)
  const [specExists, setSpecExists] = useState<boolean | null>(null) // null = still checking
  const [frsContent, setFrsContent] = useState<string | null>(null)
  const [generatingApp, setGeneratingApp] = useState(false)
  const [projectRefs, setProjectRefs] = useState<ProjectReference[]>([])
  const [selectedRefs, setSelectedRefs] = useState<Array<{ folder_name: string; owner_user_id: string }>>([])

  // Load generation mode and thinking effort from settings
  useEffect(() => {
    getSettings().then((s) => {
      setGenerationMode(s.generation_mode || 'llm')
      if (s.thinking_effort_config) setEffortConfig(s.thinking_effort_config)
    }).catch(() => { /* use default */ })
  }, [])

  // Track which agent task is active (model vs app_json)
  const agentTaskTypeRef = useRef<string>('model')

  // Claude Code agent hook for model and app generation
  const agent = useClaudeAgent({
    project,
    onComplete: async (changedFiles) => {
      if (agentTaskTypeRef.current === 'app_json') {
        const appFile = changedFiles.find((f) => f.path.includes('application.json'))
        if (appFile) {
          toast.success('Application generated by Claude Agent!')
          try {
            const data = await getJson(project)
            const appId = data?.application?.id
            if (appId && appId !== project && project === 'Draft Application') {
              await saveDraft(appId)
              await fetchProjects()
              selectProject(appId)
              navigate(`/generateapp/preview?project=${encodeURIComponent(appId)}`)
            } else {
              setAppData(data)
              if (data?.navigation) {
                const expanded: Record<string, boolean> = {}
                data.navigation.forEach((nav: NavigationItem) => {
                  expanded[nav.name] = true
                })
                setExpandedGroups(expanded)
              }
            }
          } catch {
            // Reload the page to pick up the new data
            window.location.reload()
          }
        } else {
          toast.error('Application generation completed but no file was created')
        }
        setGeneratingApp(false)
        return
      }

      // Model task
      const targetKey = agentTargetObj
      if (targetKey) {
        const hasModel = changedFiles.some((f) => f.path.includes('Metadata_Model'))
        if (hasModel) {
          // key format: "{obj_name}-{obj_type}" where obj_type is a single char
          const lastDash = targetKey.lastIndexOf('-')
          const objName = targetKey.substring(0, lastDash)
          const objType = targetKey.substring(lastDash + 1)
          try {
            await generateDatabase({ obj_name: objName, obj_type: objType, project })
          } catch {
            // post-processing failure is non-fatal
          }
          toast.success('Model generated by Claude Agent!')
          setObjectStatuses((prev) => ({
            ...prev,
            [targetKey]: { ...prev[targetKey], modelExists: true, generatingModel: false },
          }))
        }
      }
      setAgentTargetObj(null)
    },
  })

  // Load project references
  useEffect(() => {
    if (project) {
      getProjectReferences(project)
        .then((refs) => {
          setProjectRefs(refs)
          setSelectedRefs(
            refs.map((r) => ({ folder_name: r.folder_name, owner_user_id: r.owner_user_id }))
          )
        })
        .catch(() => {
          setProjectRefs([])
          setSelectedRefs([])
        })
    }
  }, [project])

  useEffect(() => {
    const loadApp = async () => {
      setLoading(true)
      try {
        const data = await getJson(project)
        setAppData(data)
        // Expand all groups by default
        if (data?.navigation) {
          const expanded: Record<string, boolean> = {}
          data.navigation.forEach((nav: NavigationItem) => {
            expanded[nav.name] = true
          })
          setExpandedGroups(expanded)
        }
      } catch {
        // No application.json — check if specification exists
        try {
          const frsData = await getFrsContent(project)
          if (frsData.content) {
            setSpecExists(true)
            setFrsContent(frsData.content)
          } else {
            setSpecExists(false)
          }
        } catch {
          setSpecExists(false)
        }
      } finally {
        setLoading(false)
      }
    }
    loadApp()
  }, [project])

  const handleGenerateAppFromFrs = useCallback(async () => {
    if (!frsContent) return

    // Claude Code Agent mode
    if (generationMode === 'claude_code') {
      agentTaskTypeRef.current = 'app_json'
      setGeneratingApp(true)
      setAgentPanelOpen(true)
      agent.startTask('app_json', {
        description: 'Generate application JSON from FRS',
        effort: effortConfig.app_json,
      })
      return
    }

    // LLM API mode (existing behavior)
    setGeneratingApp(true)
    try {
      await generateAppFromFrs(frsContent, project)
      // Read the generated app to get its ID, then auto-save (rename Draft → app ID)
      const data = await getJson(project)
      const appId = data?.application?.id
      if (appId && appId !== project && project === 'Draft Application') {
        await saveDraft(appId)
        toast.success('Application generated!')
        await fetchProjects()
        selectProject(appId)
        navigate(`/generateapp/preview?project=${encodeURIComponent(appId)}`)
      } else {
        toast.success('Application generated!')
        setAppData(data)
        if (data?.navigation) {
          const expanded: Record<string, boolean> = {}
          data.navigation.forEach((nav: NavigationItem) => {
            expanded[nav.name] = true
          })
          setExpandedGroups(expanded)
        }
      }
    } catch {
      toast.error('Failed to generate application')
    } finally {
      setGeneratingApp(false)
    }
  }, [frsContent, project, fetchProjects, selectProject, navigate, generationMode, agent])

  // Check statuses for all objects
  useEffect(() => {
    if (!appData?.navigation) return
    const checkAll = async () => {
      const statuses: Record<string, ObjectStatus> = {}
      for (const nav of appData.navigation) {
        if (!nav.objects) continue
        for (const obj of nav.objects) {
          const key = `${obj.name}-${obj.type}`
          try {
            const [modelRes, protoRes] = await Promise.all([
              checkModelStatus(project, obj.name, obj.type).catch(() => ({ exists: false })),
              checkPrototypeStatus(project, obj.name, obj.type).catch(() => ({ exists: false })),
            ])
            statuses[key] = {
              modelExists: modelRes.exists,
              prototypeExists: protoRes.exists,
              generatingModel: false,
              generatingPrototype: false,
            }
          } catch {
            statuses[key] = {
              modelExists: false,
              prototypeExists: false,
              generatingModel: false,
              generatingPrototype: false,
            }
          }
        }
      }
      setObjectStatuses(statuses)
    }
    checkAll()
  }, [appData, project])

  const handleRefsChange = useCallback(
    async (refs: Array<{ folder_name: string; owner_user_id: string }>) => {
      setSelectedRefs(refs)
      try {
        await setProjectReferences(project, refs)
        // Refresh staleness info
        const updated = await getProjectReferences(project)
        setProjectRefs(updated)
      } catch {
        toast.error('Failed to save references')
      }
    },
    [project]
  )

  const toggleGroup = (name: string) => {
    setExpandedGroups((prev) => ({ ...prev, [name]: !prev[name] }))
  }

  const handleGenerateModel = useCallback(async (obj: NavigationObject) => {
    const key = `${obj.name}-${obj.type}`

    // Three completely independent generation pipelines, each with its own
    // task_type / endpoint / service / prompt module.
    //   V (Visual)      → task_type='visual'  / generateVisualObject
    //   S (Smart Page)  → task_type='page'    / generatePageObject
    //   T / D / R / G   → task_type='model'   / generateModel
    const agentTaskType =
      obj.type === 'V' ? 'visual' : obj.type === 'S' ? 'page' : 'model'

    // Claude Code Agent mode
    if (generationMode === 'claude_code') {
      agentTaskTypeRef.current = agentTaskType
      setAgentTargetObj(key)
      setAgentPanelOpen(true)
      setObjectStatuses((prev) => ({
        ...prev,
        [key]: { ...prev[key], generatingModel: true },
      }))
      agent.startTask(agentTaskType, {
        obj_name: obj.name,
        obj_type: obj.type,
        description: obj.description || '',
        functional_specs: obj.functional_specs || '',
        project_folder: project,
        effort: effortConfig.model,
      })
      return
    }

    // LLM API mode — branch by obj_type so each type uses its own endpoint
    // and service. No shared dispatch across pipelines.
    setObjectStatuses((prev) => ({
      ...prev,
      [key]: { ...prev[key], generatingModel: true },
    }))
    try {
      if (obj.type === 'V') {
        await generateVisualObject({
          project,
          obj_name: obj.name,
          description: obj.description || '',
          functional_specs: obj.functional_specs || '',
        })
      } else if (obj.type === 'S') {
        await generatePageObject({
          project,
          obj_name: obj.name,
          description: obj.description || '',
          functional_specs: obj.functional_specs || '',
        })
      } else {
        await generateModel({
          obj_name: obj.name,
          obj_type: obj.type,
          description: obj.description || '',
          functional_specs: obj.functional_specs || '',
          project_folder: project,
        })
      }
      toast.success(`Model generated for ${obj.name}`)
      setObjectStatuses((prev) => ({
        ...prev,
        [key]: { ...prev[key], modelExists: true, generatingModel: false },
      }))
    } catch {
      toast.error(`Failed to generate model for ${obj.name}`)
      setObjectStatuses((prev) => ({
        ...prev,
        [key]: { ...prev[key], generatingModel: false },
      }))
    }
  }, [project, generationMode, agent, effortConfig])

  const handleGeneratePrototype = useCallback(async (obj: NavigationObject) => {
    const key = `${obj.name}-${obj.type}`
    setObjectStatuses((prev) => ({
      ...prev,
      [key]: { ...prev[key], generatingPrototype: true },
    }))
    try {
      await generatePrototype({
        obj_name: obj.name,
        project,
        obj_type: obj.type,
      })
      toast.success(`Preview generated for ${obj.name}`)
      setObjectStatuses((prev) => ({
        ...prev,
        [key]: { ...prev[key], prototypeExists: true, generatingPrototype: false },
      }))
    } catch {
      toast.error(`Failed to generate preview for ${obj.name}`)
      setObjectStatuses((prev) => ({
        ...prev,
        [key]: { ...prev[key], generatingPrototype: false },
      }))
    }
  }, [project])

  const handleViewPrototype = useCallback(async (obj: NavigationObject) => {
    try {
      const res = await showPrototype({
        obj_name: obj.name,
        project,
        obj_type: obj.type,
      })
      setPrototypeHtml(res.content)
      setPrototypeObjName(obj.name)
    } catch {
      toast.error('Failed to load preview')
    }
  }, [project])


  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'T': return 'Transaction'
      case 'D': return 'Dashboard'
      case 'R': return 'Report'
      case 'W': return 'Workflow'
      case 'M': return 'Master'
      default: return type
    }
  }

  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'T': return 'badge-info'
      case 'D': return 'badge-success'
      case 'R': return 'badge-warning'
      case 'W': return 'badge-error'
      default: return 'badge-info'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
          <span style={{ color: 'var(--color-text-secondary)' }}>Loading application...</span>
        </div>
      </div>
    )
  }

  if (!appData) {
    return (
      <div style={{ display: 'flex', height: '100%', gap: 0 }}>
        <div className="flex items-center justify-center" style={{ flex: 1, height: '100%' }}>
          <div className="flex flex-col items-center gap-4" style={{ textAlign: 'center', maxWidth: 420 }}>
            {specExists === null ? (
              // Still checking
              <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
            ) : specExists ? (
              // Specification exists, but no application JSON
              <>
                <FileText size={48} style={{ color: 'var(--color-accent)', opacity: 0.7 }} />
                <div>
                  <h3 style={{ fontSize: '1.1875rem', fontWeight: 600, marginBottom: 4 }}>
                    Specification Ready
                  </h3>
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', margin: 0 }}>
                    Your specification has been created. Generate the application to see the preview.
                  </p>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleGenerateAppFromFrs}
                  disabled={generatingApp}
                >
                  {generatingApp ? (
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Play size={16} />
                  )}
                  {generatingApp ? 'Generating...' : 'Generate Application'}
                </button>
              </>
            ) : (
              // No specification exists
              <>
                <Sparkles size={48} style={{ color: 'var(--color-accent)', opacity: 0.7 }} />
                <div>
                  <h3 style={{ fontSize: '1.1875rem', fontWeight: 600, marginBottom: 4 }}>
                    No Specification Yet
                  </h3>
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', margin: 0 }}>
                    Start by describing your application and generating a specification.
                  </p>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/generateapp/describe')}
                >
                  <Sparkles size={16} />
                  Generate Specification
                </button>
              </>
            )}
          </div>
        </div>

        {/* Agent Stream Panel (Claude Code mode) */}
        {agentPanelOpen && generationMode === 'claude_code' && (
          <div className="responsive-panel" style={{
            width: 420,
            flexShrink: 0,
            borderLeft: '1px solid var(--color-border)',
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideInRight 250ms ease-out',
          }}>
            <AgentStreamPanel
              messages={agent.messages}
              isStreaming={agent.isStreaming}
              wsStatus={agent.wsStatus}
              error={agent.error}
              agentProgress={agent.agentProgress}
              onCancel={() => {
                agent.cancelTask()
                setGeneratingApp(false)
              }}
              onClear={agent.clearMessages}
              onClose={() => {
                setAgentPanelOpen(false)
                setGeneratingApp(false)
              }}
              onPermissionResponse={agent.respondToPermission}
              title="Generating Application"
            />
          </div>
        )}
      </div>
    )
  }

  const app = appData.application

  return (
    <div style={{ display: 'flex', height: '100%', gap: 0 }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', gap: '1.25rem', overflow: 'auto', minWidth: 0 }}>
      {/* Prototype preview modal */}
      {prototypeHtml && (
        <div className="overlay" onClick={() => { setPrototypeHtml(null); setPrototypeObjName(null) }}>
          <div
            className="modal"
            style={{ maxWidth: 900, width: '95%', maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">Preview: {prototypeObjName}</h3>
              <button className="modal-close" onClick={() => { setPrototypeHtml(null); setPrototypeObjName(null) }}>
                <XCircle size={20} />
              </button>
            </div>
            <div style={{
              overflow: 'auto',
              maxHeight: 'calc(90vh - 120px)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              background: '#fff',
            }}>
              <iframe
                srcDoc={prototypeHtml}
                style={{ width: '100%', height: 600, border: 'none' }}
                title={`Preview - ${prototypeObjName}`}
              />
            </div>
          </div>
        </div>
      )}

      {/* App Header */}
      <div className="card" style={{ flexShrink: 0 }}>
        <div className="flex items-center gap-4">
          <AppIcon
            icon={app.icon}
            imageData={app.icon_image_data}
            size={48}
            iconSize={24}
            color="#fff"
            background={app.theme_color || 'var(--color-accent)'}
            borderRadius="var(--radius-lg)"
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: 2 }}>
              {app.title || 'Untitled Application'}
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', margin: 0 }}>
              {app.description || 'No description'}
            </p>
            {projectRefs.length > 0 && (
              <div style={{ display: 'flex', gap: '0.375rem', marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                  References:
                </span>
                {projectRefs.map((ref) => (
                  <span
                    key={`${ref.folder_name}-${ref.owner_user_id}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                      padding: '1px 8px',
                      fontSize: '0.8125rem',
                      background: ref.is_stale ? 'rgba(230, 167, 0, 0.12)' : 'rgba(0, 153, 116, 0.1)',
                      color: ref.is_stale ? '#e6a700' : 'var(--color-accent)',
                      borderRadius: 'var(--radius-full)',
                      border: `1px solid ${ref.is_stale ? 'rgba(230, 167, 0, 0.3)' : 'rgba(0, 153, 116, 0.2)'}`,
                    }}
                    title={ref.is_stale ? 'Source project has changed since reference was taken' : ''}
                  >
                    {ref.is_stale && <AlertTriangle size={9} />}
                    {ref.folder_name}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => navigate(`/generateapp/finetune?project=${encodeURIComponent(project)}`)}
            >
              <Edit3 size={14} />
              Edit Specification
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => navigate(`/autoagent?project=${encodeURIComponent(project)}`)}
              title="Auto-generate remaining models, business logic, and deploy"
              style={{ borderColor: 'var(--color-accent)', color: 'var(--color-accent)' }}
            >
              <Zap size={14} />
              AI Agent
            </button>
          </div>
        </div>
      </div>

      {/* Reference Picker — edit referenced apps */}
      <div style={{ flexShrink: 0 }}>
        <ReferenceProjectPicker
          projectId={project}
          value={selectedRefs}
          onChange={handleRefsChange}
        />
      </div>

      {/* Navigation & Objects */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {appData.navigation && appData.navigation.length > 0 ? (
          <div className="flex flex-col gap-3">
            {appData.navigation.map((nav) => (
              <div key={nav.name} className="card" style={{ padding: 0 }}>
                {/* Group header */}
                <button
                  onClick={() => toggleGroup(nav.name)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    width: '100%',
                    padding: '0.875rem 1.25rem',
                    background: 'none',
                    color: 'var(--color-text)',
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderBottom: expandedGroups[nav.name] ? '1px solid var(--color-border)' : 'none',
                    textAlign: 'left',
                  }}
                >
                  {expandedGroups[nav.name] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>
                    {nav.icon || 'folder'}
                  </span>
                  {nav.name}
                  {nav.objects && (
                    <span style={{
                      fontSize: '0.8125rem',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 400,
                      marginLeft: 'auto',
                    }}>
                      {nav.objects.length} object{nav.objects.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </button>

                {/* Objects */}
                {expandedGroups[nav.name] && nav.objects && (
                  <div style={{ padding: '0.5rem' }}>
                    {nav.objects.map((obj) => {
                      const key = `${obj.name}-${obj.type}`
                      const status = objectStatuses[key] || {
                        modelExists: false,
                        prototypeExists: false,
                        generatingModel: false,
                        generatingPrototype: false,
                      }
                      return (
                        <div
                          key={key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 4,
                            background: 'var(--color-bg)',
                          }}
                        >
                          <Box size={16} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="flex items-center gap-2">
                              <span style={{ fontWeight: 500, fontSize: '0.9375rem' }}>{obj.name}</span>
                              <span className={`badge ${getTypeBadgeClass(obj.type)}`} style={{ fontSize: '0.8125rem' }}>
                                {getTypeLabel(obj.type)}
                              </span>
                            </div>
                            {obj.description && (
                              <p style={{
                                fontSize: '0.8125rem',
                                color: 'var(--color-text-secondary)',
                                margin: '2px 0 0',
                                lineHeight: 1.4,
                              }}>
                                {obj.description}
                              </p>
                            )}
                          </div>

                          {/* Status indicators */}
                          <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                            <span title={status.modelExists ? 'Model exists' : 'No model'}>
                              {status.modelExists ? (
                                <CheckCircle2 size={14} style={{ color: 'var(--color-success, #1a7f37)' }} />
                              ) : (
                                <XCircle size={14} style={{ color: 'var(--color-text-secondary)', opacity: 0.4 }} />
                              )}
                            </span>
                            <span title={status.prototypeExists ? 'Preview exists' : 'No preview'}>
                              {status.prototypeExists ? (
                                <CheckCircle2 size={14} style={{ color: 'var(--color-info, #0969da)' }} />
                              ) : (
                                <XCircle size={14} style={{ color: 'var(--color-text-secondary)', opacity: 0.4 }} />
                              )}
                            </span>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => handleGenerateModel(obj)}
                              disabled={status.generatingModel}
                              title="Generate Model"
                            >
                              {status.generatingModel ? (
                                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                              ) : (
                                <Cpu size={14} />
                              )}
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => handleGeneratePrototype(obj)}
                              disabled={status.generatingPrototype}
                              title="Generate Preview"
                            >
                              {status.generatingPrototype ? (
                                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                              ) : (
                                <FileCode size={14} />
                              )}
                            </button>
                            {status.prototypeExists && (
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => handleViewPrototype(obj)}
                                title="View Preview"
                              >
                                <Eye size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center" style={{ height: '100%' }}>
            <p style={{ color: 'var(--color-text-secondary)' }}>No navigation items defined.</p>
          </div>
        )}
      </div>
      </div>

      {/* Agent Stream Panel (Claude Code mode) */}
      {agentPanelOpen && generationMode === 'claude_code' && (
        <div className="responsive-panel" style={{
          width: 420,
          flexShrink: 0,
          borderLeft: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInRight 250ms ease-out',
        }}>
          <AgentStreamPanel
            messages={agent.messages}
            isStreaming={agent.isStreaming}
            wsStatus={agent.wsStatus}
            error={agent.error}
            agentProgress={agent.agentProgress}
            onCancel={() => {
              agent.cancelTask()
              if (agentTaskTypeRef.current === 'app_json') {
                setGeneratingApp(false)
              } else if (agentTargetObj) {
                setObjectStatuses((prev) => ({
                  ...prev,
                  [agentTargetObj]: { ...prev[agentTargetObj], generatingModel: false },
                }))
                setAgentTargetObj(null)
              }
            }}
            onClear={agent.clearMessages}
            onClose={() => {
              setAgentPanelOpen(false)
              if (agentTaskTypeRef.current === 'app_json') {
                setGeneratingApp(false)
              } else if (agentTargetObj) {
                setObjectStatuses((prev) => ({
                  ...prev,
                  [agentTargetObj]: { ...prev[agentTargetObj], generatingModel: false },
                }))
                setAgentTargetObj(null)
              }
            }}
            onPermissionResponse={agent.respondToPermission}
            title={
              agentTaskTypeRef.current === 'app_json'
                ? 'Generating Application'
                : agentTargetObj
                  ? `Generating Model: ${agentTargetObj.split('-')[0]}`
                  : 'Generating Model'
            }
          />
        </div>
      )}
    </div>
  )
}
